import React, { useEffect, useState } from 'react';

import { MenuProps, Space } from 'antd';
import styled from 'styled-components';

import { transformCSV } from 'components/CMASummary/csv';
import { handleDownloadExcel } from 'components/CMASummary/excelUtils';
import { CMASummaryFilters } from 'components/CMASummary/types';
import { isCommunityCCRC } from 'components/Community/utils';

import DownCaretIcon from '../../../common/Icons/DownCaretIcon';
import { getFilename } from '../../../common/Widgets/GraphWidget/utils';
import { DEVICE_SIZES, STATUS } from '../../../constants';
import { useAppSelector } from '../../../store/hook';
import {
  exportElementAsImage,
  exportElementAsPDF,
  exportViewAsImageHeadless,
  exportViewAsPDFHeadless,
} from '../../../utils/helpers/export';
import { useRootContext } from '../../layout/RootContext';
import Button from '../../lib/Button';
import Dropdown from '../../lib/Dropdown';
import { useCommunityViewContext } from '../CommunityViewContext';
import ShareViaEmailModal from '../share/ShareViaEmailModal';
import { ViewFilters } from '../types';

const Container = styled.div``;

const HeaderViewActionsShare = () => {
  const { showMessage } = useRootContext();
  const { contentRef, view, preview, viewFilters, filtersModified, isViewCMA, community, competitors, CMASummaryData } =
    useCommunityViewContext();

  const { selectedCommunity } = useAppSelector((state) => state.global);

  const [openShareModal, setOpenShareModal] = useState<boolean>(false);
  const [filename, setFilename] = useState<string>('');
  const [loading, setLoading] = useState<string>(STATUS.IDLE);

  useEffect(() => {
    setFilename(getFilename(selectedCommunity?.name || 'Community', view?.name));
  }, [selectedCommunity, view]);

  const handleDownloadPDF = async () => {
    setLoading(STATUS.LOADING);
    try {
      if (window.innerWidth < DEVICE_SIZES.TABLET) {
        await exportViewAsPDFHeadless(filename, filtersModified, viewFilters as ViewFilters);
      } else {
        await exportElementAsPDF(contentRef, filename, { padding: 0 });
      }
      showMessage('success', 'View PDF download completed!');
      setLoading(STATUS.LOADED);
    } catch (e) {
      showMessage('error', 'Trouble downloading view PDF.');
      setLoading(STATUS.FAILURE);
    }
  };

  const handleDownloadImage = async () => {
    setLoading(STATUS.LOADING);
    try {
      if (window.innerWidth < DEVICE_SIZES.TABLET) {
        await exportViewAsImageHeadless(filename, filtersModified, viewFilters as ViewFilters);
      } else {
        await exportElementAsImage(contentRef, filename);
      }
      showMessage('success', 'View image download completed!');
      setLoading(STATUS.LOADED);
    } catch (e) {
      showMessage('error', 'Trouble downloading view image');
      setLoading(STATUS.FAILURE);
    }
  };

  const items: MenuProps['items'] = [];
  if (!preview) {
    items.push({
      label: 'Share via Email',
      key: 'shareEmail',
      onClick: () => setOpenShareModal(true),
    });
  }

  if (isViewCMA) {
    items.push({
      label: 'Download as Excel',
      key: 'downloadExcel',
      disabled: Boolean(!CMASummaryData?.length),
      onClick: () => handleDownloadExcel([community, ...competitors], viewFilters as CMASummaryFilters, CMASummaryData),
    }, 
    {
      label: 'Download as CSV',
      key: 'downloadCsv',
      disabled: Boolean(!CMASummaryData?.length),
      onClick: () => transformCSV(community, CMASummaryData, viewFilters as CMASummaryFilters),
    });
  }

  items.push(
    {
      label: 'Download as PDF',
      key: 'downloadPdf',
      onClick: () => handleDownloadPDF(),
    },
    {
      label: 'Download as Image',
      key: 'downloadImage',
      onClick: () => handleDownloadImage(),
    }
  );

  return (
    <Container>
      {!preview && openShareModal && (
        <ShareViaEmailModal open={openShareModal} onClose={() => setOpenShareModal(false)} />
      )}
      <Dropdown menu={{ items }}>
        <Button loading={loading === STATUS.LOADING} data-test-id="share-btn">
          <Space align="baseline">
            <span>Share</span>
            <DownCaretIcon />
          </Space>
        </Button>
      </Dropdown>
    </Container>
  );
};

export default HeaderViewActionsShare;
